/**
 * Arlo & Co — Variant Button Grid
 * Replaces the Artisan theme's variant dropdown with styled button cards.
 *
 * Artisan theme structure:
 *   div.product__variants-wrapper[data-current-variant-id]   ← outer wrapper
 *     div[data-product-option]                                ← one per option
 *       div.product__label-wrapper > label + span[data-selected-value-for-option]
 *       div.select-wrapper
 *         select[name="options[Size]"]  ← the VISIBLE select (option values)
 *   div.product-form__variants (hidden no-JS fallback)
 *     select[name="id"]                ← variant IDs, hidden by theme JS
 *
 * Config: window.ArloProductConfig.variantButtons[handle]
 *   Array of { variantId, variantTitle, description, bullets[], pricePerUnit, price }
 *
 * Requires: arlo-product-config.js loaded first.
 */

(function () {
  // ── Step coordinator (shared with arlo-options.js and arlo-photo-uploader.js) ──
  if (!window._arloStepCoord) {
    window._arloStepCoord = {
      _badges: [],
      _scheduled: false,
      register: function (el) {
        this._badges.push(el);
        if (!this._scheduled) {
          this._scheduled = true;
          var self = this;
          setTimeout(function () {
            self._badges.sort(function (a, b) {
              return (a.compareDocumentPosition(b) & 4) ? -1 : 1;
            });
            self._badges.forEach(function (b, i) { b.textContent = i + 1; });
          }, 0);
        }
      }
    };
  }
  'use strict';

  var CONTAINER_ID = 'arlo-variant-buttons';

  // 2026-10-13: page-wide price selectors ([data-price], .money …) also match the
  // product TILES further down the page ("You may also like"), the cart drawer,
  // search and menus — each showing a DIFFERENT product's price. Writing into
  // them put this product's price on every recommended product. Only ever touch
  // prices that belong to the main product.
  var OTHER_PRODUCT_AREAS = '.product-item, .quick-cart, .cross-sells, .product-card-mini, .quick-search, .predictive-search, .meganav, .mobile-menu';
  function mainPriceEls(sel) {
    return [].filter.call(document.querySelectorAll(sel), function (el) {
      return !(el.closest && el.closest(OTHER_PRODUCT_AREAS));
    });
  }

  // ── Resolve handle ────────────────────────────────────────────────────────────
  function getHandle() {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product) {
      return window.ShopifyAnalytics.meta.product.handle || null;
    }
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ── Get button configs for this product ──────────────────────────────────────
  function getConfigs() {
    var c = window.ArloProductConfig;
    if (!c || !c.variantButtons) return null;
    var handle = getHandle();
    if (!handle) return null;
    var arr = c.variantButtons[handle];
    return (arr && arr.length) ? arr : null;
  }

  // ── Fresh variant.available, straight from the live API ──────────────────────
  // `window.ShopifyAnalytics.meta.product` is embedded in the page's initial HTML
  // at render/cache time — for `.available` specifically (used to gate Add to
  // Cart), that's the wrong thing to trust: found 2026-07-21 that it read `false`
  // for a variant confirmed in Shopify Admin to be "Continue selling when out of
  // stock" with negative inventory (which Shopify itself computes as available).
  // A fresh, uncached fetch of /products/{handle}.js is the one source that can't
  // be stale — fetched once up front so it's ready by the time a button is clicked.
  var _freshVariantsById = null; // populated async; null until the fetch resolves
  function _loadFreshVariants(onLoaded) {
    var handle = getHandle();
    if (!handle) return;
    fetch('/products/' + handle + '.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var map = {};
        (data.variants || []).forEach(function (v) { map[String(v.id)] = v; });
        _freshVariantsById = map;
        if (onLoaded) onLoaded();
      })
      .catch(function () {});
  }

  // ── Full variant from ShopifyAnalytics by ID ─────────────────────────────────
  function getVariantById(vid) {
    // Prefer the fresh live-fetched copy (see _loadFreshVariants) — falls back to
    // the page-embedded ShopifyAnalytics data only if the fetch hasn't landed yet.
    if (_freshVariantsById && _freshVariantsById[String(vid)]) {
      return _freshVariantsById[String(vid)];
    }
    try {
      var variants = window.ShopifyAnalytics.meta.product.variants;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(vid)) return variants[i];
      }
    } catch (e) {}
    return null;
  }

  // ── Price map: variantId (string) → price in cents ───────────────────────────
  function getPriceMap() {
    var map = {};
    try {
      window.ShopifyAnalytics.meta.product.variants.forEach(function (v) {
        map[String(v.id)] = v.price;
      });
    } catch (e) {}
    return map;
  }

  function formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ── Live price for a button (2026-10-07) ─────────────────────────────────────
  // Shopify is the source of truth, so a compare-at sale run in Shopify shows up
  // here with no admin changes. now = the variant's live price; was = its live
  // compare_at_price, only when genuinely higher (a real markdown, sale or
  // permanent). The admin's stored salePrice/compareAtPrice are only a stand-in
  // for the moment before the fresh /products/<handle>.js lands, and only while
  // they still agree with the live price embedded in the page.
  function livePrices(cfg) {
    var vid = String(cfg.variantId);
    var fresh = _freshVariantsById && _freshVariantsById[vid];
    if (fresh && fresh.price != null) {
      var cmp = fresh.compare_at_price || 0;
      return { now: fresh.price, was: cmp > fresh.price ? cmp : 0 };
    }
    var pagePrice = getPriceMap()[vid];
    var storedNow = cfg.salePrice ? Math.round(parseFloat(cfg.salePrice) * 100) : null;
    var storedWas = cfg.compareAtPrice ? Math.round(parseFloat(cfg.compareAtPrice) * 100) : 0;
    if (pagePrice != null) {
      return { now: pagePrice, was: (storedNow === pagePrice && storedWas > pagePrice) ? storedWas : 0 };
    }
    if (storedNow != null) return { now: storedNow, was: storedWas > storedNow ? storedWas : 0 };
    return { now: cfg.price ? Math.round(parseFloat(cfg.price) * 100) : null, was: 0 };
  }

  // Badge label from the LIVE markdown (was − now). The number always comes from
  // Shopify, so the badge can never advertise a saving that isn't charged.
  // 2026-10-11: shown for EVERY reduced variant (Damian: variant cards and the
  // main price must look like the rest during a sale) — the admin's Discount
  // Badge switch no longer hides it. No markdown → no badge. Dollar is the
  // default (rounded down to 50c); percent is rounded down to a whole %.
  function arloBadgeLabel(cfg, live) {
    if (!live || live.now == null || !(live.was > live.now)) return '';
    var savings = live.was - live.now;
    if (cfg.badgeStyle === 'percent') {
      var pct = Math.floor(savings / live.was * 100);
      return pct > 0 ? 'Save ' + pct + '%' : '';
    }
    var dollars = (Math.floor(savings / 50) * 50) / 100;
    return dollars > 0 ? 'Save $' + (dollars % 1 === 0 ? dollars : dollars.toFixed(2)) : '';
  }

  // (Re)draw a button's price row and Save badge from livePrices(). Called when
  // the button is built and again once the fresh variant data arrives. The row
  // goes right under the title; the badge last, so it pins to the card bottom.
  function paintButtonPrice(btn, cfg) {
    var live = livePrices(cfg);
    var oldRow = btn.querySelector('.arlo-vb-price-row');
    if (oldRow) oldRow.parentNode.removeChild(oldRow);
    var oldBadge = btn.querySelector('.arlo-vb-badge');
    if (oldBadge) oldBadge.parentNode.removeChild(oldBadge);

    if (!cfg.hidePrice && (live.now != null || cfg.pricePerUnit)) {
      var priceRow = document.createElement('div');
      priceRow.className = 'arlo-vb-price-row';
      if (live.was) {
        var compareEl = document.createElement('span');
        compareEl.className = 'arlo-vb-compare';
        compareEl.textContent = formatPrice(live.was);
        priceRow.appendChild(compareEl);
      }
      if (live.now != null) {
        var priceEl = document.createElement('span');
        priceEl.className = 'arlo-vb-price';
        priceEl.textContent = formatPrice(live.now);
        priceRow.appendChild(priceEl);
      }
      if (cfg.pricePerUnit) {
        var puEl = document.createElement('span');
        puEl.className = 'arlo-vb-per-unit';
        puEl.textContent = cfg.pricePerUnit;
        priceRow.appendChild(puEl);
      }
      var titleEl = btn.querySelector('.arlo-vb-title');
      btn.insertBefore(priceRow, titleEl ? titleEl.nextSibling : btn.firstChild);
    }

    var label = arloBadgeLabel(cfg, live);
    if (label) {
      var badgeEl = document.createElement('span');
      // --shopify = a real Shopify markdown, which stays visible in sale mode
      // (only the theme's own invented savings tags are hidden then).
      badgeEl.className = 'arlo-vb-badge arlo-vb-badge--shopify';
      badgeEl.textContent = label;
      btn.appendChild(badgeEl);
    }
  }

  // ── Update page price display directly ───────────────────────────────────────
  var PRICE_SELECTORS = [
    '[data-price]',
    '[data-product-price]',
    '.price__current .money',
    '.price-item--regular',
    '.product__price .money',
    '.product-single__price',
  ].join(',');

  function setPriceEls(priceCents) {
    var formatted = formatPrice(priceCents);
    mainPriceEls(PRICE_SELECTORS).forEach(function (el) {
      // The theme wraps the struck-through compare-at in span.money too — never
      // write the current price into it (setCompareEls owns it).
      if (el.closest && el.closest('[data-compare-price], s')) return;
      if (/[\$£€¥]/.test(el.textContent) || el.hasAttribute('data-price') || el.hasAttribute('data-product-price')) {
        var moneyEl = el.querySelector('.money') || el;
        moneyEl.textContent = formatted;
      }
    });
  }

  function setCompareEls(compareCents) {
    var formatted = compareCents ? formatPrice(compareCents) : '';
    mainPriceEls('[data-compare-price]').forEach(function (el) {
      (el.querySelector('.money') || el).textContent = formatted;
      var s = el.closest ? el.closest('s') : null;
      if (s) s.style.display = formatted ? '' : 'none';
    });
  }

  // Show a "Save $X" badge right beside the main product price (mirrors the option
  // buttons). Reuses a single #arlo-btn-opt-badge and repositions it next to the
  // current price element. Pass a falsy label to hide it.
  function setPageSaveBadge(label) {
    var badge = document.getElementById('arlo-btn-opt-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.id = 'arlo-btn-opt-badge';
      badge.className = 'arlo-vb-badge';
      badge.style.cssText = 'display:inline-block;margin-left:8px;vertical-align:middle';
    }
    if (!label) { badge.style.display = 'none'; return; }
    // Anchor beside the CURRENT price — not the struck-through compare-at,
    // which the theme also wraps in span.money and renders first.
    var priceEl = [].filter.call(mainPriceEls(PRICE_SELECTORS), function (el) {
      return !(el.closest && el.closest('[data-compare-price], s'));
    })[0];
    if (priceEl) {
      // If the match is an inner .money span, anchor to it; otherwise the element itself.
      var anchor = priceEl;
      if (anchor.parentNode && badge.previousElementSibling !== anchor) {
        anchor.parentNode.insertBefore(badge, anchor.nextSibling);
      }
    }
    badge.textContent = label;
    badge.classList.add('arlo-vb-badge--shopify'); // real markdown — kept in sale mode
    badge.style.display = 'inline-block';
  }

  function updatePagePrice(variant, cfg) {
    if (!variant || variant.price == null) return;
    cfg = cfg || {};

    // 2026-10-07: always the LIVE Shopify price (see livePrices). The old code
    // showed the admin's stored salePrice, or invented a discount from the badge
    // percentage — both drift from what checkout charges once a sale changes the
    // Shopify price. 2026-10-11: the main price now shows the struck-through
    // "was" price + "Save $" badge, like the option-button products.
    var live = livePrices(cfg);
    var nowCents = live.now != null ? live.now : variant.price;
    var label = arloBadgeLabel(cfg, live);
    // setTimeout so our value lands after the theme's own price update.
    setTimeout(function () {
      setPriceEls(nowCents);
      setCompareEls(live.was || 0);
      setPageSaveBadge(label);
    }, 0);
  }

  // ── Drive variant selection through the Artisan theme ────────────────────────
  // The visible options select is OUTSIDE the form in Artisan — search document-wide.
  function selectVariant(cfg, form) {
    // Resolve the real variant FIRST — used below to derive the select's option
    // value from live Shopify data instead of trusting the admin-stored title.
    var variant = getVariantById(cfg.variantId);

    // 1. Update the visible options select (outside the form in Artisan)
    var optSelect = document.querySelector('select[name^="options["]');
    if (optSelect) {
      // Prefer variant.option1 (the REAL, current option value from live Shopify
      // data) over cfg.variantTitle (a string captured in the admin at whatever
      // point the button was configured). These can drift out of sync — e.g. if a
      // variant is renamed in Shopify after the admin config was set — and when
      // they do, `optSelect.value = cfg.variantTitle` fails SILENTLY (no matching
      // <option>, so the select just keeps its previous value). That single silent
      // failure cascades: Artisan's own onOptionChange re-derives "the variant"
      // from the select's (unchanged) value, gets the WRONG one, and every native
      // feature built on top of it breaks — price display goes blank (theme.js
      // hides price els when getVariantFromSerializedArray finds no match), sku,
      // discounts, etc. Found 2026-07-21 on "Photo Leather Wallet": 3 of 4 variant
      // buttons showed no price at all, exactly the 3 whose select-value
      // assignment was silently failing.
      var optionValue = (variant && variant.option1 != null) ? variant.option1 : cfg.variantTitle;
      optSelect.value = optionValue;
      optSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // 2. Update the hidden no-JS id select inside the form
    var idSelect = form.querySelector('select[name="id"]');
    if (idSelect) {
      idSelect.value = String(cfg.variantId);
      idSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // 3. Update any hidden input[name="id"] created dynamically by ProductForm()
    var idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      var nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
      nativeSetter.call(idInput, String(cfg.variantId));
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      idInput.dispatchEvent(new Event('input',  { bubbles: true }));
    }

    // 4. Fire Artisan's custom event ourselves with full variant data
    //    This is what arlo-variant-uploader.js listens to for slot updates.
    if (variant) {
      document.dispatchEvent(new CustomEvent('product:variant-change', {
        bubbles: true,
        detail: { variant: variant, userTriggered: true }
      }));
      updatePagePrice(variant, cfg);
    }

    // 5. Force the Add to Cart button's disabled state directly from the variant's
    //    own `.available` (Shopify's own field — already correctly accounts for
    //    "Continue selling when out of stock", unlike a raw inventory_quantity
    //    check). Artisan's theme.js normally does this itself in response to the
    //    'change' event dispatched in step 1 (via ProductForm -> onOptionChange ->
    //    updateBuyButton) — but that chain depends on getVariantFromSerializedArray()
    //    re-deriving the variant from optSelect's live value, which has proven
    //    unreliable for button-driven selection (found 2026-07-21: continue-selling
    //    variants with negative stock were left showing as unavailable/disabled on
    //    this product, even though `variant.available` was correctly true). Setting
    //    it directly here — from the exact variant object we already resolved above
    //    — sidesteps that chain entirely and can't get out of sync with it.
    if (variant) {
      var atcBtn = form.querySelector('[data-add-to-cart]');
      if (atcBtn) {
        var atcText = atcBtn.querySelector('[data-add-to-cart-text]');
        if (variant.available) {
          atcBtn.removeAttribute('disabled');
          atcBtn.removeAttribute('aria-disabled');
          if (atcText && atcBtn.dataset.langAvailable) atcText.textContent = atcBtn.dataset.langAvailable;
        } else {
          atcBtn.setAttribute('disabled', 'disabled');
          atcBtn.setAttribute('aria-disabled', 'true');
          if (atcText && atcBtn.dataset.langSoldOut) atcText.textContent = atcBtn.dataset.langSoldOut;
        }
      }
    }
  }

  // ── Get initial selected variant ID ──────────────────────────────────────────
  function getInitialVariantId(form) {
    // Artisan sets data-current-variant-id on the variants wrapper (outside form)
    var wrapper = document.querySelector('[data-current-variant-id]');
    if (wrapper && wrapper.dataset.currentVariantId) {
      return String(wrapper.dataset.currentVariantId);
    }
    // Fallback: no-JS select[name="id"] inside the form
    var idSelect = form.querySelector('select[name="id"]');
    if (idSelect && idSelect.value) return String(idSelect.value);
    // Last resort: ShopifyAnalytics
    try {
      var v = window.ShopifyAnalytics.meta.product.selected_or_first_available_variant;
      if (v && v.id) return String(v.id);
    } catch (e) {}
    return null;
  }

  // ── Hide the Artisan variant picker ──────────────────────────────────────────
  // Artisan renders the picker OUTSIDE the form as a section block.
  // Search document-wide.
  function hidePicker() {
    var wrapper = document.querySelector('.product__variants-wrapper');
    if (wrapper) {
      wrapper.style.setProperty('display', 'none', 'important');
      return;
    }
    // Fallback: hide each individual option block
    document.querySelectorAll('[data-product-option]').forEach(function (el) {
      el.style.setProperty('display', 'none', 'important');
    });
  }

  // ── Inject styles ─────────────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('arlo-vb-styles')) return;
    var s = document.createElement('style');
    s.id = 'arlo-vb-styles';
    s.textContent = `
      #arlo-variant-buttons {
        margin-bottom: 20px;
        width: 100%;
        box-sizing: border-box;
        overflow: hidden;
      }
      /* Horizontal scroll row — few items fill the width, many scroll across.
         align-items:stretch makes every card the tallest card's height so they
         don't jump up/down as you scroll. */
      .arlo-vb-grid {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 8px;
        width: 100%;
        box-sizing: border-box;
        align-items: stretch;
        scroll-snap-type: x proximity;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 6px;
      }
      .arlo-vb-grid::-webkit-scrollbar { height: 6px; }
      .arlo-vb-grid::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }
      .arlo-vb-item {
        position: relative;
        display: flex;
        flex: 1 0 140px;                 /* grow to fill when few; min 140px then scroll */
        scroll-snap-align: start;
      }
      .arlo-vb-btn {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: 74px;              /* slightly deeper cards */
        padding: 11px 11px 11px;       /* a little more breathing room */
        border: none;
        box-shadow: inset 0 0 0 1.5px var(--color-border, #d1d1d1);
        border-radius: var(--radius, 6px);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        text-align: left;
        cursor: pointer;
        transition: box-shadow 0.15s;
        box-sizing: border-box;
        font-family: inherit;
      }
      .arlo-vb-btn:hover {
        box-shadow: inset 0 0 0 1.5px var(--color-button-primary-background, #111);
      }
      .arlo-vb-btn.active {
        box-shadow: inset 0 0 0 2.5px var(--color-button-primary-background, #111) !important;
        background-color: color-mix(in srgb, var(--color-button-primary-background, #111) 15%, transparent) !important;
      }
      @supports not (color: color-mix(in srgb, red 15%, transparent)) {
        .arlo-vb-btn.active { background-color: rgba(0,0,0,0.07) !important; }
      }
      .arlo-vb-btn.sold-out {
        opacity: 0.45;
        cursor: not-allowed;
      }
      .arlo-vb-title {
        font-size: 13px;
        font-weight: 700;
        line-height: 1.2;
        margin-bottom: 3px;
        letter-spacing: -0.01em;
      }
      .arlo-vb-price-row {
        display: flex;
        align-items: baseline;
        gap: 5px;
        margin-bottom: 4px;
      }
      .arlo-vb-price {
        font-size: 13px;
        font-weight: 600;
        color: var(--color-foreground, #111);
      }
      .arlo-vb-per-unit {
        font-size: 10px;
        color: var(--color-foreground-secondary, #666);
        font-weight: 400;
      }
      .arlo-vb-desc {
        font-size: 10px;
        color: var(--color-foreground-secondary, #666);
        line-height: 1.3;
        margin-top: 1px;
        text-align: center;
      }
      .arlo-vb-help {
        position: absolute;
        top: 6px;
        right: 6px;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 1.5px dashed var(--color-border, #aaa);
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 700;
        color: var(--color-foreground-secondary, #888);
        cursor: pointer;
        line-height: 1;
        padding: 0;
        z-index: 2;
        transition: border-color 0.15s, color 0.15s;
      }
      .arlo-vb-help:hover,
      .arlo-vb-help.open {
        border-color: var(--color-foreground, #111);
        color: var(--color-foreground, #111);
      }
      .arlo-vb-bullets {
        display: none;
        grid-column: 1 / -1;
        background: var(--color-background-secondary, #f8f8f8);
        border: 1px solid var(--color-border, #d1d1d1);
        border-radius: var(--radius, 6px);
        padding: 12px 16px;
        margin-top: 2px;
      }
      .arlo-vb-bullets.open { display: block; }
      .arlo-vb-bullets ul {
        margin: 0;
        padding: 0 0 0 18px;
        list-style: disc;
      }
      .arlo-vb-bullets li {
        font-size: 13px;
        line-height: 1.6;
        color: var(--color-foreground, #111);
        margin-bottom: 2px;
      }
      .arlo-vb-compare {
        font-size: 11px;
        color: var(--color-foreground-secondary, #888);
        text-decoration: line-through;
        font-weight: 400;
      }
      .arlo-vb-badge {
        display: inline-block;
        margin-top: auto;        /* pin to the bottom of the (equal-height) card */
        padding: 2px 7px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        background: #b05a3a;
        color: #fff;
        line-height: 1.4;
        align-self: flex-start;
      }
      .arlo-vb-bullets-title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--color-foreground-secondary, #666);
        margin-bottom: 8px;
      }
      /* Step 1 header — matches arlo-options.js step badge style */
      .arlo-opt-label {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--color-foreground, #111);
      }
      .arlo-opt-step-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--color-button-primary-background, #111);
        color: var(--color-button-primary-text, #fff);
        font-size: 13px;
        font-weight: 700;
        flex-shrink: 0;
      }
    `;
    document.head.appendChild(s);
  }

  // ── Build and insert the button grid ─────────────────────────────────────────
  function buildButtons(configs, form) {
    var existing = document.getElementById(CONTAINER_ID);
    if (existing) existing.remove();

    var openBulletsId = null;
    var currentVariantId = getInitialVariantId(form);

    var container = document.createElement('div');
    container.id  = CONTAINER_ID;

    // Step header — coordinator assigns correct number after all scripts have rendered
    var stepHeader = document.createElement('label');
    stepHeader.className = 'arlo-opt-label';
    stepHeader.style.marginBottom = '12px';
    var vbBadge = document.createElement('span');
    vbBadge.className = 'arlo-opt-step-badge';
    vbBadge.textContent = '';
    stepHeader.appendChild(vbBadge);
    stepHeader.appendChild(document.createTextNode('Choose your option'));
    if (window._arloStepCoord) window._arloStepCoord.register(vbBadge);
    container.appendChild(stepHeader);

    var grid = document.createElement('div');
    grid.className = 'arlo-vb-grid';
    container.appendChild(grid);

    var bulletsPanel = document.createElement('div');
    bulletsPanel.className = 'arlo-vb-bullets';
    container.appendChild(bulletsPanel);

    configs.forEach(function (cfg) {
      var vid      = String(cfg.variantId);
      var isActive = (vid === currentVariantId);
      var hasBullets = cfg.bullets && cfg.bullets.length;

      var item = document.createElement('div');
      item.className = 'arlo-vb-item';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'arlo-vb-btn' + (isActive ? ' active' : '');
      btn.dataset.variantId = vid;
      if (hasBullets) btn.style.paddingRight = '26px';

      // Title — use buttonLabel if set, otherwise fall back to variantTitle
      var titleEl = document.createElement('div');
      titleEl.className = 'arlo-vb-title';
      titleEl.textContent = cfg.buttonLabel || cfg.variantTitle || '';
      btn.appendChild(titleEl);


      // Description
      if (cfg.description) {
        var descEl = document.createElement('div');
        descEl.className = 'arlo-vb-desc';
        descEl.textContent = cfg.description;
        btn.appendChild(descEl);
      }

      // Price row (under the title) + Save badge (last, pinned to the card
      // bottom) — from live Shopify prices; repainted when fresh data lands.
      paintButtonPrice(btn, cfg);

      item.appendChild(btn);

      // "?" help badge
      if (hasBullets) {
        var helpBtn = document.createElement('button');
        helpBtn.type = 'button';
        helpBtn.className = 'arlo-vb-help';
        helpBtn.textContent = '?';
        helpBtn.setAttribute('aria-label', 'More info');

        helpBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          if (openBulletsId === vid) {
            openBulletsId = null;
            bulletsPanel.classList.remove('open');
            helpBtn.classList.remove('open');
          } else {
            grid.querySelectorAll('.arlo-vb-help.open').forEach(function (h) { h.classList.remove('open'); });
            openBulletsId = vid;
            bulletsPanel.innerHTML = '';
            if (cfg.variantTitle) {
              var t = document.createElement('div');
              t.className = 'arlo-vb-bullets-title';
              t.textContent = cfg.variantTitle;
              bulletsPanel.appendChild(t);
            }
            var ul = document.createElement('ul');
            (cfg.bullets || []).forEach(function (b) {
              var li = document.createElement('li');
              li.textContent = b;
              ul.appendChild(li);
            });
            bulletsPanel.appendChild(ul);
            bulletsPanel.classList.add('open');
            helpBtn.classList.add('open');
          }
        });

        item.appendChild(helpBtn);
      }

      // Click: select variant
      btn.addEventListener('click', function () {
        if (btn.classList.contains('sold-out')) return;
        grid.querySelectorAll('.arlo-vb-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        currentVariantId = vid;
        selectVariant(cfg, form);
      });

      grid.appendChild(item);
    });

    // Insert inside the form, before whichever arlo element comes first:
    // #arlo-uploader (photo upload step) or #arlo-options-container (text/monogram steps).
    // This ensures step 1 always appears at the top of the personalisation area
    // regardless of product configuration.
    function findFirstArloAnchor() {
      // #arlo-options-container is always a direct form child
      var opts = form.querySelector('#arlo-options-container');

      // #arlo-uploader may be nested inside a wrapper — walk up to form child
      var uploaderEl = form.querySelector('#arlo-uploader');
      var uploaderAnchor = uploaderEl;
      while (uploaderAnchor && uploaderAnchor.parentNode !== form) {
        uploaderAnchor = uploaderAnchor.parentNode;
      }

      if (!opts && !uploaderAnchor) return null;
      if (!opts) return uploaderAnchor;
      if (!uploaderAnchor) return opts;
      // Both exist — return whichever appears earlier in the DOM
      return (uploaderAnchor.compareDocumentPosition(opts) & Node.DOCUMENT_POSITION_FOLLOWING)
        ? uploaderAnchor : opts;
    }

    var anchor = findFirstArloAnchor();
    if (anchor) {
      form.insertBefore(container, anchor);
    } else {
      // Fallback: before qty+ATC wrapper or ATC button
      var qtyWrapper = form.querySelector('.product-form__quantity-and-buy-buttons-wrapper');
      if (qtyWrapper) {
        form.insertBefore(container, qtyWrapper);
      } else {
        var atcBtn = form.querySelector('[data-add-to-cart]') || form.querySelector('[type="submit"]');
        if (atcBtn && atcBtn.parentNode === form) form.insertBefore(container, atcBtn);
        else form.appendChild(container);
      }
    }

    // ── Ensure something is always active on load, and SYNC it ────────────────
    // A button may already be .active because Shopify restored the variant on
    // refresh. In that case we must still run selectVariant() so the uploader
    // count (and price/badge) match the restored variant — otherwise the button
    // shows e.g. "16 Decals" but the uploader falls back to the default count.
    var activeBtn = grid.querySelector('.arlo-vb-btn.active');
    if (!activeBtn) {
      var firstAvail = grid.querySelector('.arlo-vb-btn:not(.sold-out)');
      if (firstAvail) {
        firstAvail.classList.add('active');
        activeBtn = firstAvail;
      }
    }
    if (activeBtn) {
      currentVariantId = activeBtn.dataset.variantId;
      var activeCfg = configs.find(function (c) { return String(c.variantId) === currentVariantId; });
      if (activeCfg) selectVariant(activeCfg, form);
    }

    // Keep active state in sync with external changes (e.g., theme routing)
    document.addEventListener('product:variant-change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      var newId = String(variant.id);
      if (newId === currentVariantId) return; // fired by us — ignore
      currentVariantId = newId;
      grid.querySelectorAll('.arlo-vb-btn').forEach(function (b) {
        b.classList.toggle('active', b.dataset.variantId === newId);
      });
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    // Always inject styles — arlo-options.js uses .arlo-vb-btn classes for its button type
    injectStyles();

    var configs = getConfigs();
    if (!configs) return;

    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // The Artisan theme renders variant pickers as blocks OUTSIDE the <form>,
    // so search the whole document.
    var variantWrapper = document.querySelector('.product__variants-wrapper');
    var optionSelect   = document.querySelector('select[name^="options["]');
    if (!variantWrapper && !optionSelect) return; // single-variant or unsupported

    buildButtons(configs, form);
    hidePicker();

    // The very first selectVariant() call inside buildButtons() runs before this
    // fetch can possibly resolve, so it may fall back to the page-embedded
    // ShopifyAnalytics data for .available. Once the fresh copy lands, re-apply it
    // for whichever variant ended up active — corrects the Add to Cart button if
    // the fallback data disagreed with the live API.
    _loadFreshVariants(function () {
      // Fresh prices: repaint every card (a sale may have changed them since the
      // page HTML was cached), then re-apply the active one for the page price.
      document.querySelectorAll('#' + CONTAINER_ID + ' .arlo-vb-btn').forEach(function (b) {
        var c = configs.find(function (x) { return String(x.variantId) === b.dataset.variantId; });
        if (c) paintButtonPrice(b, c);
      });
      var activeBtn = document.querySelector('#' + CONTAINER_ID + ' .arlo-vb-btn.active');
      if (!activeBtn) return;
      var activeCfg = configs.find(function (c) { return String(c.variantId) === activeBtn.dataset.variantId; });
      if (activeCfg) selectVariant(activeCfg, form);
    });
  }

  function waitForConfig(cb, attempts) {
    attempts = attempts || 0;
    if (window.ArloProductConfig) return cb();
    if (attempts > 30) return;
    setTimeout(function () { waitForConfig(cb, attempts + 1); }, 100);
  }

  document.addEventListener('DOMContentLoaded', function () {
    waitForConfig(init);
  });

})();
